import { CredentialsAndIdentityId, CredentialsAndIdentityIdProvider, GetCredentialsOptions } from '@aws-amplify/auth';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, switchMap } from 'rxjs';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';
import { iotDataFeature } from '@core/iot-data/iot-data.reducer';
import { amplifyCredentialProviderActions, cognitoIdentityApiActions } from '@core/iot-data/iot-data.actions';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { distinctUntilChanged, filter, take, tap } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { selectCurrentUser } from '@core/auth2/store/auth.selectors';

@Injectable({
  providedIn: 'any'
})
export class AmplifyCredentialsProviderService implements CredentialsAndIdentityIdProvider {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  constructor() {
    combineLatest([
      this.store.select(selectCurrentUser)
        .pipe(notNullish(), distinctUntilChanged((a, b) => a.issuer === b.issuer && a.subject === b.subject)),
      this.store.select(userTenantFeature.selectSelectedTenantId)
        .pipe(notNullish(), distinctUntilChanged())
    ]).pipe(
    ).subscribe(() => this.store.dispatch(amplifyCredentialProviderActions.userOrTenantChanged()));
  }

  clearCredentialsAndIdentityId(): void {
    this.store.dispatch(amplifyCredentialProviderActions.clearCredentialsAndIdentityId());
  }

  getCredentialsAndIdentityId(getCredentialsOptions: GetCredentialsOptions): Promise<CredentialsAndIdentityId | undefined> {
    let nowPlus5Seconds: Date;

    const latestCredentialsAndIdentityId$ = this.store
      .select(iotDataFeature.selectAwsCredentials)
      .pipe(
        notNullish(),
        tap((credentialsAndId) => {
          nowPlus5Seconds = new Date(Date.now() + 5000);
          if (nowPlus5Seconds > credentialsAndId.credentials.expiration)
            this.store.dispatch(amplifyCredentialProviderActions.credentialsExpiring());
        }),
        filter(credentialsAndId => nowPlus5Seconds <= credentialsAndId.credentials.expiration),
      );

    if (getCredentialsOptions.forceRefresh) {
      const waitForRefresh$ = this.actions$.pipe(
        ofType(cognitoIdentityApiActions.getCredentialsForIdentitySuccess),
        take(1),
      );

      this.store.dispatch(amplifyCredentialProviderActions.forceCredentialsRefreshRequested());

      return firstValueFrom(waitForRefresh$.pipe(
        switchMap(() => latestCredentialsAndIdentityId$),
      ));
    }

    return firstValueFrom(latestCredentialsAndIdentityId$);
  }
}
